import apiClient from './apiClient';

// Magnet interface matching both frontend and backend structure
export interface Magnet {
  id: string;
  title: string;
  description: string;
  stage: 'STAGE_1' | 'STAGE_2' | 'STAGE_3' | 'STAGE_4' | 'STAGE_5';
  type: 'WHITEPAPER' | 'CASE_STUDY' | 'EBOOK' | 'WEBINAR' | 'GUIDE' | 'CHECKLIST';
  format: 'PDF' | 'VIDEO' | 'SLIDE_DECK' | 'INTERACTIVE';
  journeySteps: number[];
  downloads: number;
  deliveries: number;
  createdDate: string;
  author: string;
  // File attachments
  pdfFile?: string;
  pdfFileName?: string;
  pptFile?: string;
  pptFileName?: string;
  imageFile?: string;
  imageFileName?: string;
  videoLink?: string;
}

export type CreateMagnetData = Omit<Magnet, 'id' | 'downloads' | 'deliveries' | 'createdDate'>;

export interface MagnetsResponse {
  data: Magnet[];
  total: number;
  page: number;
  totalPages: number;
}

// API Service Functions
export const magnetService = {
  /**
   * Get all magnets with optional filtering and pagination
   */
  async getAllMagnets(
    category?: string,
    page: number = 1,
    limit: number = 100
  ): Promise<MagnetsResponse> {
    try {
      const params: any = { page, limit };
      if (category) {
        params.category = category;
      }

      const response = await apiClient.get<MagnetsResponse>('/api/magnets', { params });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching magnets:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch magnets');
    }
  },

  /**
   * Get a single magnet by ID
   */
  async getMagnetById(id: string): Promise<Magnet> {
    try {
      const response = await apiClient.get<Magnet>(`/api/magnets/${id}`);
      console.log('Magnet fetched successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching magnet:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch magnet');
    }
  },

  /**
   * Create a new magnet with all data including attachments
   */
  async createMagnet(magnetData: CreateMagnetData): Promise<Magnet> {
    try {
      const response = await apiClient.post<Magnet>('/api/magnets', magnetData);
      console.log('Magnet created successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating magnet:', error);
      throw new Error(error.response?.data?.message || 'Failed to create magnet');
    }
  },

  /**
   * Update an existing magnet
   */
  async updateMagnet(id: string, magnetData: Partial<Magnet>): Promise<Magnet> {
    try {
      const response = await apiClient.put<Magnet>(`/api/magnets/${id}`, magnetData);
      console.log('Magnet updated successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating magnet:', error);
      throw new Error(error.response?.data?.message || 'Failed to update magnet');
    }
  },

  /**
   * Delete a magnet
   */
  async deleteMagnet(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/magnets/${id}`);
      console.log('Magnet deleted successfully');
    } catch (error: any) {
      console.error('Error deleting magnet:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete magnet');
    }
  },
};

export default magnetService;
